import React, { useState, useEffect } from 'react';
import { Plus, Loader2, Edit2, Trash2 } from 'lucide-react';
import { apiFetch } from '../../utils/api';

const AccountsManagement: React.FC = () => {
    const [accounts, setAccounts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const [showEditModal, setShowEditModal] = useState(false);

    // Add Modal State
    const [showAddModal, setShowAddModal] = useState(false);
    const [newData, setNewData] = useState({
        name: '', contact_number: '', address: '', city: '', state: '', country: ''
    });


    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/accounts');
            if (res.ok) {
                setAccounts(await res.json());
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, []); // Removed token from dependency array as apiFetch handles auth internally

    const handleEditClick = (account: any) => {
        setEditData(account);
        setShowEditModal(true);
    };

    const handleDeleteClick = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this account?')) return;
        try {
            const res = await apiFetch(`/accounts/${id}`, {
                method: 'DELETE',
            });
            if (res.ok) {
                alert('Account deleted successfully');
                fetchAccounts();
            } else {
                alert('Failed to delete account');
            }
        } catch (err) {
            console.error(err);
            alert('Error deleting account');
        }
    };

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editData || !editData.id) return; // Ensure editData and its id exist
        try {
            const res = await apiFetch(`/accounts/${editData.id}`, {
                method: 'PUT',
                body: JSON.stringify(editData)
            });
            if (res.ok) {
                alert('Account updated successfully');
                setShowEditModal(false);
                fetchAccounts();
            } else {
                alert('Failed to update account');
            }
        } catch (err) {
            console.error(err);
            alert('Error updating account');
        }
    };

    const handleAddAccount = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await apiFetch('/accounts', {
                method: 'POST',
                body: JSON.stringify(newData)
            });
            if (res.ok) {
                alert('Account created successfully');
                setShowAddModal(false);
                setNewData({ name: '', contact_number: '', address: '', city: '', state: '', country: '' });
                fetchAccounts();
            } else {
                alert('Failed to create account');
            }
        } catch (err) {
            console.error(err);
            alert('Error creating account');
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Accounts Management</h2>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                    <Plus size={18} /> Add Account
                </button>
            </div>
            {loading && !showEditModal ? <Loader2 className="animate-spin" /> : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50/50">
                            <tr>
                                <th className="px-6 py-4">Name</th>
                                <th className="px-6 py-4">Contact</th>
                                <th className="px-6 py-4">City</th>
                                <th className="px-6 py-4">Country</th>
                                <th className="px-6 py-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {accounts.map(a => (
                                <tr key={a.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 font-bold">{a.name}</td>
                                    <td className="px-6 py-4">{a.contact_number}</td>
                                    <td className="px-6 py-4">{a.city}</td>
                                    <td className="px-6 py-4">{a.country}</td>
                                    <td className="px-6 py-4 flex gap-4">
                                        <button onClick={() => handleEditClick(a)} className="text-blue-600 hover:text-blue-800" title="Edit">
                                            <Edit2 size={18} />
                                        </button>
                                        <button onClick={() => handleDeleteClick(a.id)} className="text-red-500 hover:text-red-700" title="Delete">
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && editData && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl p-8 max-w-2xl w-full">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold text-gray-800">Edit Account</h3>
                            <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">Close</button>
                        </div>
                        <form onSubmit={handleUpdate} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Account Name</label>
                                <input className="w-full border p-2 rounded mt-1" value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Contact Number</label>
                                <input className="w-full border p-2 rounded mt-1" value={editData.contact_number} onChange={e => setEditData({ ...editData, contact_number: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Address</label>
                                <input className="w-full border p-2 rounded mt-1" value={editData.address} onChange={e => setEditData({ ...editData, address: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">City</label>
                                    <input className="w-full border p-2 rounded mt-1" value={editData.city} onChange={e => setEditData({ ...editData, city: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">State</label>
                                    <input className="w-full border p-2 rounded mt-1" value={editData.state} onChange={e => setEditData({ ...editData, state: e.target.value })} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Country</label>
                                <input className="w-full border p-2 rounded mt-1" value={editData.country} onChange={e => setEditData({ ...editData, country: e.target.value })} />
                            </div>
                            <div className="flex justify-end gap-4 pt-4">
                                <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 text-gray-600 font-bold hover:bg-gray-100 rounded-lg">Cancel</button>
                                <button type="submit" className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700">Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create Account Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl p-8 max-w-2xl w-full">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold text-gray-800">Add New Account</h3>
                            <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-gray-700">Close</button>
                        </div>
                        <form onSubmit={handleAddAccount} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Account Name *</label>
                                <input className="w-full border p-2 rounded mt-1" value={newData.name} onChange={e => setNewData({ ...newData, name: e.target.value })} required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Contact Number</label>
                                <input className="w-full border p-2 rounded mt-1" value={newData.contact_number} onChange={e => setNewData({ ...newData, contact_number: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Address</label>
                                <input className="w-full border p-2 rounded mt-1" value={newData.address} onChange={e => setNewData({ ...newData, address: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">City</label>
                                    <input className="w-full border p-2 rounded mt-1" value={newData.city} onChange={e => setNewData({ ...newData, city: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">State</label>
                                    <input className="w-full border p-2 rounded mt-1" value={newData.state} onChange={e => setNewData({ ...newData, state: e.target.value })} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Country</label>
                                <input className="w-full border p-2 rounded mt-1" value={newData.country} onChange={e => setNewData({ ...newData, country: e.target.value })} />
                            </div>
                            <div className="flex justify-end gap-4 pt-4">
                                <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 text-gray-600 font-bold hover:bg-gray-100 rounded-lg">Cancel</button>
                                <button type="submit" className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700">Create Account</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountsManagement;
